/**
 * LivePerson script provided by Bish Labib <blabib@australiansuper.com> from AusSuper.
 * LivePerson contact is Sandeep Karuppuswamy <skaruppuswamy@liveperson.com>.
 *
 * Generated from LivePerson account/dashboard.
 * Ref: https://knowledge.liveperson.com/getting-started-add-the-liveperson-tag-to-your-website.html
 */

import React, { useEffect, useState } from 'react';
import { useModal, useAppContext } from 'core/hooks';
import { LIVE_PERSON_MODAL_FORM, MODAL_ARIA_NAMES } from 'core/constants';
import { isStorybook } from 'stories/utils';
import * as Typo from 'core/styles/Typography';

import LPModalForm from './subcomponents/LPModalForm';
import LPModalFormConfirm from './subcomponents/LPModalFormConfirm';
import { IFields } from './LivePerson.sitecore';

const LivePerson = ({ fields }: { fields: IFields }) => {
	const [LivePersonModal, toggleLivePersonModal] = useModal(false);
	const [isModalFormLoading, setIsModalFormLoading] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [submitedServiceNumber, setSubmitedServiceNumber] = useState('');
	const [isServiceIdError, setIsServiceIdError] = useState(false);

	const { isShowLivePersonModal } = useAppContext();

	useEffect(() => {
		if (isShowLivePersonModal || isStorybook()) {
			toggleLivePersonModal(true);
		} else {
			setSubmitedServiceNumber('');
			setIsModalFormLoading(false);
			setIsServiceIdError(false);
			toggleLivePersonModal(false);
		}
	}, [isShowLivePersonModal]);

	const onServiceIdSubmit = (serviceId: string) => {
		setIsModalFormLoading(true);
		setSubmitedServiceNumber(serviceId);
		if (isStorybook()) {
			setIsModalFormLoading(false);
			setShowConfirm(true);
		} else if (!!window.lpTag.taglets) {
			window.lpTag.taglets.cobrowse.loadApi(function(visitorApi: any) {
				visitorApi.once('sessionRejected', function() {
					setIsServiceIdError(true);
					setIsModalFormLoading(false);
					const serviceIdInput = document.getElementById(
						'service-number.service-number',
					) as HTMLElement;
					serviceIdInput.focus();
					serviceIdInput.blur();
					serviceIdInput.onfocus = (_ev: FocusEvent) => {
						setIsServiceIdError(false);
					};
				});
				visitorApi.once('sessionOffered', function(event: any) {
					document.addEventListener('lpConfirm', (e: any) => {
						if (e.detail.isAccepted) {
							visitorApi.acceptSupportOffer(event);
						} else {
							visitorApi.cancelSupportOffer(event);
						}
						setSubmitedServiceNumber('');
					});
					setIsModalFormLoading(false);
					setShowConfirm(true);
				});

				//Todo: Remove during clean up phase
				function sessionReadyCallback(startEvent: any) {
					if (startEvent) {
						console.log('Session is ready and will be started shortly.');
					}
				}
				visitorApi.requestSupport({ serviceId }, sessionReadyCallback);
			});
		}
	};

	const onConfirm = (isAccepted: boolean) => {
		const lpConfirm = new CustomEvent('lpConfirm', { detail: { isAccepted } });
		document.dispatchEvent(lpConfirm);
		// setIsAccepted(isAccepted);
		setShowConfirm(false);
	};

	return (
		<LivePersonModal modalStyle={{ content: { maxWidth: '530px' } }}>
			<div>
				<Typo.DisplayMedium id={MODAL_ARIA_NAMES.HEADING}>
					{fields?.liveAssistHeading?.value ?? LIVE_PERSON_MODAL_FORM.HEADING}
				</Typo.DisplayMedium>
				{showConfirm ? (
					<LPModalFormConfirm fields={fields} onConfirm={onConfirm} />
				) : (
					<LPModalForm
						fields={fields}
						onSubmit={onServiceIdSubmit}
						loading={isModalFormLoading}
						serviceNumber={submitedServiceNumber}
						isServiceIdError={isServiceIdError}
					/>
				)}
			</div>
		</LivePersonModal>
	);
};

export default LivePerson;
